import { RequireRole, useSessionTokenGetter } from '@develio/auth/client';
import type { ProjectTypeOverrides } from '@develio/shared';
import { useCallback, useEffect, useState } from 'react';
import { BEAAttribution } from '../components/BEAAttribution';
import { type CsvRow, downloadCsvFile, parseCsv, pickCsvFile, rowsToCsv } from '../lib/csv';

/**
 * Methodology Profiles — ADR-029 Phase 3 admin UI.
 *
 * Owner-only CRUD for `assumption_profiles`. Holds Step 5 growth/depreciation,
 * Step 7 multipliers + capture + displacement, and Step 8 discount rate.
 *
 * Mirrors SettingsJurisdictionRoute in shape. A "Requires signed authorization"
 * banner surfaces once an active profile exists — Phase 3+ will actually gate
 * mutations behind a `profile_change` authorization_record; for now it's
 * advisory while the API still accepts any owner edit.
 */

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

type Profile = {
  id: string;
  name: string;
  // Step 5
  realPropertyAppreciationRate: string;
  bppDepreciationRate: string;
  inventoryGrowthRate: string;
  // Step 7 — multipliers
  operationsEmploymentMultiplier: string;
  operationsEarningsMultiplier: string;
  constructionEmploymentMultiplierJobsPerMillion: string;
  constructionEarningsMultiplier: string;
  constructionOutputMultiplier: string;
  // Step 7 — capture / spending
  localConstructionCaptureRate: string;
  workerLocalCaptureRate: string;
  workerTaxableSpendingShare: string;
  materialsShareOfConstruction: string;
  materialsTaxabilityRate: string;
  permitFeeRate: string;
  displacementRate: string;
  // Step 7 — relocation / household
  workerRelocationPct: string;
  averageHouseholdSize: string;
  schoolAgeChildrenPerHousehold: string;
  // Step 8
  discountRate: string;
  isDefault: boolean;
  grandfathered: boolean;
  isSample?: boolean;
  // ADR-030 — per-project-type overrides (JSONB)
  projectTypeOverrides: ProjectTypeOverrides | null;
};

/**
 * ADR-030 — per-project-type override fields. Stored as strings in form
 * state (matching the rest of this form); empty string means "no override,
 * inherit from the base profile value for this row above."
 */
const OVERRIDE_FIELDS = [
  { key: 'displacementRate', label: 'Displacement rate', step: 0.01 },
  { key: 'workerRelocationPct', label: 'Worker relocation %', step: 0.01 },
  { key: 'staffingRampYear1', label: 'Staffing ramp Year 1', step: 0.05 },
  { key: 'staffingRampYear2', label: 'Staffing ramp Year 2', step: 0.05 },
  { key: 'revenueRampYear1', label: 'Revenue ramp Year 1', step: 0.05 },
  { key: 'revenueRampYear2', label: 'Revenue ramp Year 2', step: 0.05 },
  {
    key: 'realPropertyAppreciationRate',
    label: 'Real property appreciation',
    step: 0.005,
  },
] as const;

type OverrideKey = (typeof OVERRIDE_FIELDS)[number]['key'];
type OverrideStrings = Record<OverrideKey, string>;

const EMPTY_OVERRIDE: OverrideStrings = {
  displacementRate: '',
  workerRelocationPct: '',
  staffingRampYear1: '',
  staffingRampYear2: '',
  revenueRampYear1: '',
  revenueRampYear2: '',
  realPropertyAppreciationRate: '',
};

const EMPTY_FORM = {
  name: '',
  realPropertyAppreciationRate: '0.020000',
  bppDepreciationRate: '0.100000',
  inventoryGrowthRate: '0.020000',
  operationsEmploymentMultiplier: '1.720000',
  operationsEarningsMultiplier: '1.600000',
  constructionEmploymentMultiplierJobsPerMillion: '12.000000',
  constructionEarningsMultiplier: '0.450000',
  constructionOutputMultiplier: '1.800000',
  localConstructionCaptureRate: '0.500000',
  workerLocalCaptureRate: '0.500000',
  workerTaxableSpendingShare: '0.600000',
  materialsShareOfConstruction: '0.500000',
  materialsTaxabilityRate: '0.800000',
  permitFeeRate: '0.005000',
  displacementRate: '0.100000',
  workerRelocationPct: '0.200000',
  averageHouseholdSize: '2.5000',
  schoolAgeChildrenPerHousehold: '0.5000',
  discountRate: '0.050000',
  isDefault: false,
  overridesExpansion: { ...EMPTY_OVERRIDE },
  overridesRetention: { ...EMPTY_OVERRIDE },
};

type FormState = typeof EMPTY_FORM;

/** Read a `ProjectTypeOverrides` block into the string-based form slot. */
function blockToStrings(block: ProjectTypeOverrides['expansion'] | undefined): OverrideStrings {
  const out: OverrideStrings = { ...EMPTY_OVERRIDE };
  if (!block) return out;
  for (const { key } of OVERRIDE_FIELDS) {
    const v = block[key];
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = String(v);
  }
  return out;
}

/** Collapse the string-based form slot back to a Zod-ready block or undefined. */
function stringsToBlock(strings: OverrideStrings): ProjectTypeOverrides['expansion'] | undefined {
  const block: Record<string, number> = {};
  for (const { key } of OVERRIDE_FIELDS) {
    const raw = strings[key];
    if (raw === undefined || raw === '') continue;
    const n = Number(raw);
    if (!Number.isFinite(n)) continue;
    block[key] = n;
  }
  return Object.keys(block).length > 0 ? (block as ProjectTypeOverrides['expansion']) : undefined;
}

function Field({
  label,
  name,
  value,
  onChange,
  type = 'text',
  step,
  hint,
}: {
  label: string;
  name: string;
  value: string | boolean;
  onChange: (name: string, val: string | boolean) => void;
  type?: 'text' | 'number' | 'checkbox';
  step?: number;
  hint?: string;
}) {
  if (type === 'checkbox') {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(name, e.target.checked)}
          className="rounded border-slate-300"
        />
        {label}
        {hint && <span className="text-xs text-slate-400">{hint}</span>}
      </label>
    );
  }
  return (
    <div>
      <label htmlFor={name} className="block text-xs font-medium text-slate-600">
        {label}
      </label>
      <input
        id={name}
        type={type}
        step={step}
        value={String(value)}
        onChange={(e) => onChange(name, e.target.value)}
        className="mt-0.5 block w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
      />
      {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="col-span-2 mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
      {children}
    </p>
  );
}

function OverridePanel({
  title,
  slot,
  values,
  onChange,
}: {
  title: string;
  slot: 'overridesExpansion' | 'overridesRetention';
  values: OverrideStrings;
  onChange: (
    slot: 'overridesExpansion' | 'overridesRetention',
    name: OverrideKey,
    val: string,
  ) => void;
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</p>
      <div className="space-y-2">
        {OVERRIDE_FIELDS.map((f) => (
          <div key={f.key}>
            <label
              htmlFor={`${slot}-${f.key}`}
              className="block text-xs font-medium text-slate-600"
            >
              {f.label}
            </label>
            <input
              id={`${slot}-${f.key}`}
              type="number"
              step={f.step}
              value={values[f.key]}
              onChange={(e) => onChange(slot, f.key, e.target.value)}
              placeholder="inherit"
              className="mt-0.5 block w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function MethodologyProfilesPanel() {
  const getToken = useSessionTokenGetter();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const fetchProfiles = useCallback(async () => {
    const token = await getToken();
    const res = await fetch(`${API}/api/v1/assumption-profiles`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`${res.status}`);
    const json = await res.json();
    setProfiles(json.profiles as Profile[]);
  }, [getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await fetchProfiles();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchProfiles]);

  function setField(name: string, val: string | boolean) {
    setForm((f) => ({ ...f, [name]: val }));
  }

  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setError(null);
  }

  function openEdit(p: Profile) {
    setEditingId(p.id);
    setForm({
      name: p.name,
      realPropertyAppreciationRate: p.realPropertyAppreciationRate,
      bppDepreciationRate: p.bppDepreciationRate,
      inventoryGrowthRate: p.inventoryGrowthRate,
      operationsEmploymentMultiplier: p.operationsEmploymentMultiplier,
      operationsEarningsMultiplier: p.operationsEarningsMultiplier,
      constructionEmploymentMultiplierJobsPerMillion:
        p.constructionEmploymentMultiplierJobsPerMillion,
      constructionEarningsMultiplier: p.constructionEarningsMultiplier,
      constructionOutputMultiplier: p.constructionOutputMultiplier,
      localConstructionCaptureRate: p.localConstructionCaptureRate,
      workerLocalCaptureRate: p.workerLocalCaptureRate,
      workerTaxableSpendingShare: p.workerTaxableSpendingShare,
      materialsShareOfConstruction: p.materialsShareOfConstruction,
      materialsTaxabilityRate: p.materialsTaxabilityRate,
      permitFeeRate: p.permitFeeRate,
      displacementRate: p.displacementRate,
      workerRelocationPct: p.workerRelocationPct,
      averageHouseholdSize: p.averageHouseholdSize,
      schoolAgeChildrenPerHousehold: p.schoolAgeChildrenPerHousehold,
      discountRate: p.discountRate,
      isDefault: p.isDefault,
      overridesExpansion: blockToStrings(p.projectTypeOverrides?.expansion),
      overridesRetention: blockToStrings(p.projectTypeOverrides?.retention),
    });
    setShowForm(true);
    setError(null);
  }

  function setOverrideField(
    which: 'overridesExpansion' | 'overridesRetention',
    name: OverrideKey,
    val: string,
  ) {
    setForm((f) => ({ ...f, [which]: { ...f[which], [name]: val } }));
  }

  async function handleSave() {
    if (!form.name) {
      setError('Profile name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const url = editingId
        ? `${API}/api/v1/assumption-profiles/${editingId}`
        : `${API}/api/v1/assumption-profiles`;
      const method = editingId ? 'PUT' : 'POST';
      // Assemble the JSON body: flatten the numeric fields the API already
      // handles, plus a structured `projectTypeOverrides` blob assembled
      // from the two string-backed slots.
      const { overridesExpansion, overridesRetention, ...flat } = form;
      const expansionBlock = stringsToBlock(overridesExpansion);
      const retentionBlock = stringsToBlock(overridesRetention);
      const projectTypeOverrides: ProjectTypeOverrides = {};
      if (expansionBlock) projectTypeOverrides.expansion = expansionBlock;
      if (retentionBlock) projectTypeOverrides.retention = retentionBlock;
      const body = { ...flat, projectTypeOverrides };
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as { error?: string }).error ?? `${res.status}`);
      }
      await fetchProfiles();
      setShowForm(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  // ── CSV template download / upload ──────────────────────────────────────
  // Flat keys mirror the form. Project-type overrides use
  // `overrides.expansion.<field>` / `overrides.retention.<field>` paths so
  // the CSV round-trips losslessly.

  const FLAT_KEYS = [
    'name',
    'realPropertyAppreciationRate',
    'bppDepreciationRate',
    'inventoryGrowthRate',
    'operationsEmploymentMultiplier',
    'operationsEarningsMultiplier',
    'constructionEmploymentMultiplierJobsPerMillion',
    'constructionEarningsMultiplier',
    'constructionOutputMultiplier',
    'localConstructionCaptureRate',
    'workerLocalCaptureRate',
    'workerTaxableSpendingShare',
    'materialsShareOfConstruction',
    'materialsTaxabilityRate',
    'permitFeeRate',
    'displacementRate',
    'workerRelocationPct',
    'averageHouseholdSize',
    'schoolAgeChildrenPerHousehold',
    'discountRate',
    'isDefault',
  ] as const;

  function handleDownloadTemplate() {
    const source = profiles.find((p) => p.isDefault) ?? profiles[0] ?? null;
    const rows: CsvRow[] = [];
    if (source) {
      rows.push(['name', source.name]);
      for (const k of FLAT_KEYS) {
        if (k === 'name') continue;
        if (k === 'isDefault') {
          rows.push([k, source.isDefault ? 'true' : 'false']);
        } else {
          rows.push([k, String((source as unknown as Record<string, string>)[k] ?? '')]);
        }
      }
      const exp = blockToStrings(source.projectTypeOverrides?.expansion);
      const ret = blockToStrings(source.projectTypeOverrides?.retention);
      for (const f of OVERRIDE_FIELDS) rows.push([`overrides.expansion.${f.key}`, exp[f.key]]);
      for (const f of OVERRIDE_FIELDS) rows.push([`overrides.retention.${f.key}`, ret[f.key]]);
    } else {
      const emptyBag = EMPTY_FORM as unknown as Record<string, unknown>;
      for (const k of FLAT_KEYS) {
        const v = emptyBag[k];
        rows.push([k, typeof v === 'boolean' ? String(v) : typeof v === 'string' ? v : '']);
      }
      for (const f of OVERRIDE_FIELDS) rows.push([`overrides.expansion.${f.key}`, '']);
      for (const f of OVERRIDE_FIELDS) rows.push([`overrides.retention.${f.key}`, '']);
    }
    const filename = 'Methodology_Template.csv';
    downloadCsvFile(filename, rowsToCsv(rows));
  }

  async function handleUploadCsv() {
    const text = await pickCsvFile();
    if (!text) return;
    try {
      const parsed = parseCsv(text);
      const next: FormState = {
        ...EMPTY_FORM,
        overridesExpansion: { ...EMPTY_OVERRIDE },
        overridesRetention: { ...EMPTY_OVERRIDE },
      };
      const bag = next as unknown as Record<string, string | boolean>;
      for (const key of FLAT_KEYS) {
        const raw = parsed[key];
        if (raw === undefined) continue;
        if (key === 'isDefault') {
          bag[key] = raw === 'true' || raw === '1';
        } else {
          bag[key] = raw;
        }
      }
      for (const f of OVERRIDE_FIELDS) {
        const ek = `overrides.expansion.${f.key}`;
        const rk = `overrides.retention.${f.key}`;
        const eVal = parsed[ek];
        const rVal = parsed[rk];
        if (eVal !== undefined) next.overridesExpansion[f.key] = eVal;
        if (rVal !== undefined) next.overridesRetention[f.key] = rVal;
      }
      setEditingId(null);
      setForm(next);
      setShowForm(true);
      setError(null);
    } catch (e) {
      setError(`Could not parse CSV: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function handleDelete(id: string) {
    const token = await getToken();
    await fetch(`${API}/api/v1/assumption-profiles/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchProfiles();
  }

  async function handleSetDefault(id: string) {
    const token = await getToken();
    await fetch(`${API}/api/v1/assumption-profiles/${id}/set-default`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchProfiles();
  }

  const hasActiveProfile = profiles.some((p) => p.isDefault);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Methodology Profiles</h2>
          <p className="mt-1 text-sm text-slate-500">
            Governed assumptions that drive Step 5, 7, and 8 of every analysis. Analysts cannot
            diverge from the active profile at analysis time.
          </p>
        </div>
        {!showForm && (
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="whitespace-nowrap rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Download template
            </button>
            <button
              type="button"
              onClick={() => void handleUploadCsv()}
              className="whitespace-nowrap rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Upload CSV
            </button>
            <button
              type="button"
              onClick={openCreate}
              className="whitespace-nowrap rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
            >
              + Add profile
            </button>
          </div>
        )}
      </div>

      {hasActiveProfile && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong className="font-semibold">Requires signed authorization.</strong> Changes to
          methodology values now require an active{' '}
          <code className="mx-1 rounded bg-amber-100 px-1 py-0.5 font-mono text-xs">
            profile_change
          </code>
          authorization record. Record one at{' '}
          <a href="/settings/governance" className="font-medium underline">
            Settings → Governance
          </a>{' '}
          before saving edits.
        </div>
      )}

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      {showForm && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-base font-semibold text-slate-800">
            {editingId ? 'Edit profile' : 'New profile'}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Profile name"
              name="name"
              value={form.name}
              onChange={setField}
              hint="e.g. Texas Standard (2026)"
            />

            <SectionHeading>Step 5 — Growth &amp; Depreciation</SectionHeading>
            <Field
              label="Real property appreciation rate"
              name="realPropertyAppreciationRate"
              value={form.realPropertyAppreciationRate}
              onChange={setField}
              type="number"
              step={0.001}
              hint="e.g. 0.020 (2%/yr)"
            />
            <Field
              label="BPP depreciation rate"
              name="bppDepreciationRate"
              value={form.bppDepreciationRate}
              onChange={setField}
              type="number"
              step={0.001}
              hint="e.g. 0.100 (10%/yr)"
            />
            <Field
              label="Inventory growth rate"
              name="inventoryGrowthRate"
              value={form.inventoryGrowthRate}
              onChange={setField}
              type="number"
              step={0.001}
              hint="e.g. 0.020"
            />

            <SectionHeading>Step 7 — RIMS Multipliers</SectionHeading>
            <div className="col-span-2">
              <BEAAttribution variant="rims" />
            </div>
            <Field
              label="Operations employment multiplier"
              name="operationsEmploymentMultiplier"
              value={form.operationsEmploymentMultiplier}
              onChange={setField}
              type="number"
              step={0.01}
            />
            <Field
              label="Operations earnings multiplier"
              name="operationsEarningsMultiplier"
              value={form.operationsEarningsMultiplier}
              onChange={setField}
              type="number"
              step={0.01}
            />
            <Field
              label="Construction employment multiplier (jobs / $M)"
              name="constructionEmploymentMultiplierJobsPerMillion"
              value={form.constructionEmploymentMultiplierJobsPerMillion}
              onChange={setField}
              type="number"
              step={0.1}
            />
            <Field
              label="Construction earnings multiplier"
              name="constructionEarningsMultiplier"
              value={form.constructionEarningsMultiplier}
              onChange={setField}
              type="number"
              step={0.01}
            />
            <Field
              label="Construction output multiplier"
              name="constructionOutputMultiplier"
              value={form.constructionOutputMultiplier}
              onChange={setField}
              type="number"
              step={0.01}
            />

            <SectionHeading>Step 7 — Capture &amp; Spending</SectionHeading>
            <Field
              label="Local construction capture rate"
              name="localConstructionCaptureRate"
              value={form.localConstructionCaptureRate}
              onChange={setField}
              type="number"
              step={0.01}
            />
            <Field
              label="Worker local capture rate"
              name="workerLocalCaptureRate"
              value={form.workerLocalCaptureRate}
              onChange={setField}
              type="number"
              step={0.01}
            />
            <Field
              label="Worker taxable spending share"
              name="workerTaxableSpendingShare"
              value={form.workerTaxableSpendingShare}
              onChange={setField}
              type="number"
              step={0.01}
            />
            <Field
              label="Materials share of construction"
              name="materialsShareOfConstruction"
              value={form.materialsShareOfConstruction}
              onChange={setField}
              type="number"
              step={0.01}
            />
            <Field
              label="Materials taxability rate"
              name="materialsTaxabilityRate"
              value={form.materialsTaxabilityRate}
              onChange={setField}
              type="number"
              step={0.01}
            />
            <Field
              label="Permit fee rate"
              name="permitFeeRate"
              value={form.permitFeeRate}
              onChange={setField}
              type="number"
              step={0.0005}
            />
            <Field
              label="Displacement rate"
              name="displacementRate"
              value={form.displacementRate}
              onChange={setField}
              type="number"
              step={0.01}
              hint="Fraction of new jobs displacing existing locals"
            />

            <SectionHeading>Step 7 — Worker Households</SectionHeading>
            <Field
              label="Worker relocation %"
              name="workerRelocationPct"
              value={form.workerRelocationPct}
              onChange={setField}
              type="number"
              step={0.01}
              hint="Fraction of workers relocating into the jurisdiction"
            />
            <Field
              label="Average household size"
              name="averageHouseholdSize"
              value={form.averageHouseholdSize}
              onChange={setField}
              type="number"
              step={0.1}
            />
            <Field
              label="School-age children per household"
              name="schoolAgeChildrenPerHousehold"
              value={form.schoolAgeChildrenPerHousehold}
              onChange={setField}
              type="number"
              step={0.1}
            />

            <SectionHeading>Step 8 — NPV</SectionHeading>
            <Field
              label="Discount rate"
              name="discountRate"
              value={form.discountRate}
              onChange={setField}
              type="number"
              step={0.005}
              hint="e.g. 0.050 (5%)"
            />

            <SectionHeading>
              Project-type overrides{' '}
              <span className="font-normal normal-case text-slate-400">
                (leave blank to inherit base values above)
              </span>
            </SectionHeading>
            <div className="col-span-2 grid grid-cols-2 gap-6">
              <OverridePanel
                title="Expansion"
                slot="overridesExpansion"
                values={form.overridesExpansion}
                onChange={setOverrideField}
              />
              <OverridePanel
                title="Retention"
                slot="overridesRetention"
                values={form.overridesRetention}
                onChange={setOverrideField}
              />
            </div>

            <div className="col-span-2">
              <Field
                label="Set as default profile"
                name="isDefault"
                value={form.isDefault}
                onChange={setField}
                type="checkbox"
                hint="Pre-fills Steps 5, 7, 8 on every new analysis"
              />
            </div>
          </div>
          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && profiles.length === 0 && !showForm && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
          <p className="text-sm text-slate-500">No methodology profiles yet.</p>
          <p className="mt-1 text-xs text-slate-400">
            Add one to lock Steps 5, 7, and 8 for all org analyses.
          </p>
        </div>
      )}

      {!loading && profiles.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Profile</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Ops Emp ×</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Discount</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Displacement</th>
                <th className="px-4 py-3 text-left font-medium text-slate-500">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {profiles.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">
                    {p.name}
                    {p.isSample && (
                      <span className="ml-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                        Sample
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {Number(p.operationsEmploymentMultiplier).toFixed(3)}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {(Number(p.discountRate) * 100).toFixed(2)}%
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {(Number(p.displacementRate) * 100).toFixed(1)}%
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {p.isDefault && (
                        <span className="inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                          Default
                        </span>
                      )}
                      {p.grandfathered && (
                        <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          Grandfathered
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!p.isDefault && (
                      <button
                        type="button"
                        onClick={() => handleSetDefault(p.id)}
                        className="mr-3 text-xs text-slate-500 hover:text-slate-900 hover:underline"
                      >
                        Set default
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => openEdit(p)}
                      className="mr-3 text-xs text-slate-500 hover:text-slate-900 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(p.id)}
                      className="text-xs text-red-500 hover:text-red-700 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SettingsMethodologyRoute() {
  return (
    <RequireRole
      requiredRole="owner"
      fallback={
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Methodology Profiles</h2>
            <p className="mt-1 text-sm text-slate-500">
              Governed assumptions that drive Steps 5, 7, and 8 of every analysis.
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-amber-50 p-4 text-sm text-amber-900">
            Methodology profiles are visible to Owners only. Contact your org owner to change locked
            methodology values.
          </div>
        </div>
      }
    >
      <MethodologyProfilesPanel />
    </RequireRole>
  );
}
